import React from 'react';

import {
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import useLinkAthleteGLM from 'hooks/glmLink/useLinkAthleteGLM';
import useSearchGLMContacts, { GLM_CONTACT_SEARCH_MIN_CHARS } from 'hooks/glmLink/useSearchGLMContacts';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { AthleteGLMContact } from 'types';
import notificationHandler from 'utils/notifications';

interface GLMContactSearchModalProps {
  opened: boolean;
  onClose: () => void;
  athleteId: string;
}

const ContactRow = ({
  contact,
  linking,
  onLink,
}: {
  contact: AthleteGLMContact;
  linking: boolean;
  onLink: (contactId: string) => void;
}): JSX.Element => {
  const name = `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();
  return (
    <Group position='apart' noWrap p='xs' style={{ borderBottom: '1px solid #eee' }}>
      <Box style={{ minWidth: 0 }}>
        <Group spacing='xs' noWrap>
          <Text size='sm' truncate>
            {name || (
              <Text component='span' color='dimmed'>
                (no name)
              </Text>
            )}
          </Text>
          {contact.alreadyLinked && (
            <Badge color='gray' size='xs'>
              Linked to another athlete
            </Badge>
          )}
        </Group>
        <Text size='xs' color='dimmed' truncate>
          {contact.email ?? '-'}
          {contact.phone ? ` · ${contact.phone}` : ''}
        </Text>
      </Box>
      <Button
        size='xs'
        variant='light'
        disabled={contact.alreadyLinked || linking}
        loading={linking}
        onClick={() => onLink(contact.contactId)}
      >
        Link
      </Button>
    </Group>
  );
};

const GLMContactSearchModal = ({ opened, onClose, athleteId }: GLMContactSearchModalProps): JSX.Element => {
  const [email, setEmail] = React.useState('');
  const [debouncedEmail] = useDebouncedValue(email, 250);
  const [linkingId, setLinkingId] = React.useState<string | null>(null);
  const { successNotification, errorNotification } = notificationHandler();
  const { trackAthleteGLMLinked } = useStaffTracking();

  const { data, isFetching, isError } = useSearchGLMContacts(opened ? athleteId : null, debouncedEmail);
  const { mutate: linkAthlete } = useLinkAthleteGLM();
  const contacts = data?.data ?? [];

  React.useEffect(() => {
    if (opened) {
      setEmail('');
      setLinkingId(null);
    }
  }, [opened]);

  const handleLink = (contactId: string): void => {
    setLinkingId(contactId);
    linkAthlete(
      { athleteId, contactId },
      {
        onSuccess: () => {
          trackAthleteGLMLinked({ athlete_id: athleteId, source: 'search_modal' });
          successNotification('Contact linked', 'The athlete is now linked to the GLM contact.');
          onClose();
        },
        onError: error => {
          errorNotification('Link failed', error.response?.data?.message);
        },
        onSettled: () => setLinkingId(null),
      },
    );
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={onClose}
      title={<Title order={4}>Link to existing GLM contact</Title>}
      size='lg'
    >
      <Stack spacing='sm'>
        <TextInput
          icon={<IconSearch size={16} />}
          placeholder='Filter contacts by email'
          value={email}
          onChange={event => setEmail(event.currentTarget.value)}
          autoFocus
        />
        <ScrollArea style={{ height: 320 }}>
          {debouncedEmail.trim().length < GLM_CONTACT_SEARCH_MIN_CHARS ? (
            <Center h={300}>
              <Text color='dimmed' size='sm'>
                {`Type at least ${GLM_CONTACT_SEARCH_MIN_CHARS} characters to search GLM contacts`}
              </Text>
            </Center>
          ) : isFetching ? (
            <Center h={300}>
              <Stack align='center' spacing='xs'>
                <Loader />
                <Text color='dimmed' size='sm'>
                  This could take a while, please be patient
                </Text>
              </Stack>
            </Center>
          ) : isError ? (
            <Center h={300}>
              <Text color='red' size='sm'>
                Search failed. Try again.
              </Text>
            </Center>
          ) : contacts.length === 0 ? (
            <Center h={300}>
              <Text color='dimmed' size='sm'>
                No matching contacts
              </Text>
            </Center>
          ) : (
            <Stack spacing={0}>
              {contacts.map(contact => (
                <ContactRow
                  key={contact.contactId}
                  contact={contact}
                  linking={linkingId === contact.contactId}
                  onLink={handleLink}
                />
              ))}
            </Stack>
          )}
        </ScrollArea>
      </Stack>
    </Modal>
  );
};

export default GLMContactSearchModal;
